import React, { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/auth-store';
import { sessionManager } from '../services/session-manager';
import { Modal, InfoIconWithBackground } from '@tasama/ui';
import { useTranslation } from '@tasama/i18n';

interface SessionTimeoutWarningProps {
  testID?: string | undefined;
  inactivityTimeout?: number; // Configurable inactivity timeout in milliseconds
}

/**
 * SessionTimeoutWarning component that shows a warning modal
 * when the user is inactive for a specified period
 * Note: Token refresh is handled automatically by the API client
 */
export const SessionTimeoutWarning: React.FC<SessionTimeoutWarningProps> = ({
  testID = 'session-timeout-warning',
  inactivityTimeout = 5 * 60 * 1000, // Default 5 minutes
}) => {
  const { t } = useTranslation('portal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const { logout, isAuthenticated } = useAuthStore();

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Cleanup function for intervals
  const clearAllIntervals = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  // Reset modal state completely
  const resetModalState = () => {
    setIsModalOpen(false);
    setTimeLeft(0);
    clearAllIntervals();
  };

  // Initialize session manager with configurable timeout
  useEffect(() => {
    if (!isAuthenticated) {
      resetModalState();
      sessionManager.stopMonitoring();
      return;
    }

    // Configure session manager with the provided timeout
    sessionManager.updateConfig({
      inactivityTimeout,
      warningBeforeTimeout: 30 * 1000, // 30 seconds warning
    });

    // Set up event handlers for inactivity
    sessionManager.setEventHandlers({
      onInactivityWarning: () => {
        const remainingTime = sessionManager.getTimeUntilTimeout();
        setTimeLeft(Math.ceil(remainingTime / 1000));
        setIsModalOpen(true);
      },
      onInactivityLogout: () => {
        resetModalState();
        logout();
      },
    });

    // Start monitoring
    sessionManager.startMonitoring();

    return () => {
      sessionManager.stopMonitoring();
    };
  }, [isAuthenticated, inactivityTimeout, logout]);

  useEffect(() => {
    // Clear existing countdown
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    if (isModalOpen && timeLeft > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            resetModalState();
            logout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isModalOpen, timeLeft, logout]);

  // Reset when authentication status changes
  useEffect(() => {
    if (!isAuthenticated) {
      resetModalState();
    }
  }, [isAuthenticated]);

  const handleExtendSession = () => {
    // User activity - reset inactivity timer
    sessionManager.extendSession();
    resetModalState();
  };

  const handleLogout = () => {
    resetModalState();
    logout();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Modal
      isOpen={isModalOpen && isAuthenticated}
      closeOnOverlayClick={false}
      closeOnEscape={false}
      testID={testID}
    >
      <Modal.Header
        type="left"
        title={t('session.inactivityWarning')}
        description={t('session.inactivityDescription', {
          time: formatTime(timeLeft),
        })}
        icon={
          <InfoIconWithBackground
            size="large"
            testID={`${testID}-modal-icon`}
          />
        }
        showCloseButton={false}
        testID={`${testID}-header`}
      />
      <Modal.Footer
        divider
        alignment="right"
        actions={[
          {
            label: t('session.logout'),
            variant: 'tertiary',
            size: 'sm',
            onClick: handleLogout,
            testID: `${testID}-logout-button`,
            iconPosition: 'none',
          },
          {
            label: t('session.stayLoggedIn'),
            variant: 'secondary',
            size: 'sm',
            onClick: handleExtendSession,
            testID: `${testID}-extend-button`,
            iconPosition: 'trailing',
            ctaIcon: 'ArrowRight',
          },
        ]}
        testID={`${testID}-footer`}
      />
    </Modal>
  );
};

export default SessionTimeoutWarning;
